import {
  isJust,
  just,
  nothing,
  type Maybe,
  matchMaybe,
  toMaybe,
} from "../maybe";
import { displayFileName } from "../fileNames";
import {
  PROJECT_CHAPTERS_DIR,
  PROJECT_DATABASE_DIR,
  PROJECT_NOTES_DIR,
} from "./types";
import type {
  ManuscriptItem,
  ZwAct,
  ZwChapter,
  ZwMapFigure,
  ZwScene,
} from "./types";

/** Matches the auto-generated `scene-{5 random hex chars}.md` / `.zwdoc` default — anything
 * else is a custom name. */
const DEFAULT_SCENE_FILE_NAME = /^scene-[0-9a-f]{5}\.(md|zwdoc)$/;

export function isDefaultSceneFileName(file: string): boolean {
  return DEFAULT_SCENE_FILE_NAME.test(file);
}

/** Custom title override, else the ordinal "Scene N" label (N = 1-based position
 * in the chapter's scene list). */
export function sceneDisplayName(scene: ZwScene, index: number): string {
  return isDefaultSceneFileName(scene.file)
    ? `Scene ${index + 1}`
    : displayFileName(scene.file);
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeZwScene(raw: unknown): Maybe<ZwScene> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const file = asString(obj.file).trim();
  if (file === "") return nothing;
  return just({ file, synopsis: asString(obj.synopsis) });
}

function normalizeZwChapter(raw: unknown): Maybe<ZwChapter> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const folder = asString(obj.folder).trim();
  if (folder === "") return nothing;
  const indexTrimmed = asString(obj.index).trim();
  const index = indexTrimmed !== "" ? indexTrimmed : "index.zwdoc";
  const scenes: ZwScene[] = [];
  if (Array.isArray(obj.scenes)) {
    for (const rawScene of obj.scenes) {
      const sceneM = normalizeZwScene(rawScene);
      if (isJust(sceneM)) scenes.push(sceneM.value);
    }
  }
  if (scenes.length === 0) return nothing;
  const synopsis = asString(obj.synopsis);
  return just({
    folder,
    name: asString(obj.name).trim(),
    index,
    scenes,
    synopsis,
  });
}

export function normalizeZwChapters(raw: unknown): ZwChapter[] {
  if (Array.isArray(raw) === false) return [];
  const chapters: ZwChapter[] = [];
  for (const entry of raw) {
    const chapterM = normalizeZwChapter(entry);
    if (isJust(chapterM)) chapters.push(chapterM.value);
  }
  return chapters;
}

function normalizeZwAct(raw: unknown): Maybe<ZwAct> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const folder = asString(obj.folder).trim();
  if (folder === "") return nothing;
  const indexTrimmed = asString(obj.index).trim();
  return just({
    folder,
    name: asString(obj.name).trim(),
    index: indexTrimmed !== "" ? indexTrimmed : "index.zwdoc",
    // Unlike a chapter, an act with nothing under it is a valid state.
    chapters: normalizeZwChapters(obj.chapters),
  });
}

function normalizeZwMapFigure(raw: unknown): Maybe<ZwMapFigure> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  const title = asString(obj.title).trim();
  const mapPath = asString(obj.mapPath).trim();
  if (id === "" || title === "" || mapPath === "") return nothing;
  const orientation = normalizeMapOrientation(obj.orientation);
  const spreadRaw = asString(obj.spread).trim();
  const spread = spreadRaw === "spread" ? "spread" : "single";
  const printExtent = normalizeMapPrintExtent(obj.printExtent);
  const objectFit = normalizeMapObjectFit(obj.objectFit);
  return just({
    id,
    title,
    mapPath,
    orientation,
    spread,
    printExtent,
    objectFit,
  });
}

/** Closed set: 0, 90, 180, or 270. Anything else becomes 0. */
export function normalizeMapOrientation(raw: unknown): 0 | 90 | 180 | 270 {
  if (typeof raw !== "number" || Number.isFinite(raw) === false) return 0;
  const n = ((Math.round(raw) % 360) + 360) % 360;
  if (n === 90 || n === 180 || n === 270) return n;
  return 0;
}

/** Maps default to safeZone (not text margins). */
export function normalizeMapPrintExtent(
  raw: unknown,
): "textMargins" | "safeZone" | "fullBleed" {
  const s = asString(raw).trim();
  if (s === "textMargins" || s === "fullBleed") return s;
  return "safeZone";
}

/** Maps default to contain (letterbox). */
export function normalizeMapObjectFit(raw: unknown): "contain" | "cover" {
  return asString(raw).trim() === "cover" ? "cover" : "contain";
}

/**
 * Reads the manuscript order out of a raw `.zwconfig`-shaped object, migrating
 * the legacy flat `chapters` array when `manuscript` is absent. A top-level entry
 * with no `type` is treated as a chapter, so a hand-edited config that omits the
 * tag still opens.
 */
export function normalizeManuscript(
  rawManuscript: unknown,
  legacyChapters?: unknown,
): ManuscriptItem[] {
  if (Array.isArray(rawManuscript)) {
    const items: ManuscriptItem[] = [];
    for (const raw of rawManuscript) {
      if (raw === null || typeof raw !== "object") continue;
      const type = asString((raw as Record<string, unknown>).type).trim();
      if (type === "act") {
        const actM = normalizeZwAct(raw);
        if (isJust(actM)) items.push({ type: "act", ...actM.value });
        continue;
      }
      if (type === "map") {
        const mapM = normalizeZwMapFigure(raw);
        if (isJust(mapM)) items.push({ type: "map", ...mapM.value });
        continue;
      }
      const chapterM = normalizeZwChapter(raw);
      if (isJust(chapterM)) items.push({ type: "chapter", ...chapterM.value });
    }
    return items;
  }
  return normalizeZwChapters(legacyChapters).map((chapter) => ({
    type: "chapter" as const,
    ...chapter,
  }));
}

/** Every chapter in manuscript order with act nesting flattened away. */
export function flattenChapters(manuscript: ManuscriptItem[]): ZwChapter[] {
  const chapters: ZwChapter[] = [];
  for (const item of manuscript) {
    if (item.type === "chapter") chapters.push(item);
    else if (item.type === "act") chapters.push(...item.chapters);
  }
  return chapters;
}

/** Every act in manuscript order. */
export function actsInOrder(manuscript: ManuscriptItem[]): ZwAct[] {
  return manuscript.filter(
    (item): item is { type: "act" } & ZwAct => item.type === "act",
  );
}

/** Row/card identity for a chapter — its first scene's path (stable regardless
 * of scene count). */
export function chapterPrimaryPath(chapter: ZwChapter): string {
  return `${PROJECT_CHAPTERS_DIR}/${chapter.folder}/${chapter.scenes[0]?.file ?? ""}`;
}

/** Virtual row/card key, retaining the legacy path shape for saved collapse
 * state. This identifies metadata, not an act document on disk. */
export function actPrimaryPath(act: ZwAct): string {
  const index = act.index !== "" ? act.index : "index.zwdoc";
  return `${PROJECT_CHAPTERS_DIR}/${act.folder}/${index}`;
}

export function scenePath(folder: string, sceneFile: string): string {
  return `${PROJECT_CHAPTERS_DIR}/${folder}/${sceneFile}`;
}

/** First scene in manuscript order, or Nothing when the outline is empty. */
export function firstManuscriptScenePath(
  manuscript: Maybe<ManuscriptItem[]>,
): Maybe<string> {
  const chapters = flattenChapters(
    matchMaybe(manuscript, {
      nothing: () => [] as ManuscriptItem[],
      just: (items) => items,
    }),
  );
  for (const chapter of chapters) {
    const fileM = toMaybe(chapter.scenes[0]?.file);
    if (isJust(fileM) && fileM.value !== "") {
      return just(scenePath(chapter.folder, fileM.value));
    }
  }
  return nothing;
}

/** Ordered `Chapters/<folder>/<scene file>` paths for an already-flat chapter
 * list, for callers that were handed chapters rather than the manuscript tree. */
export function scenePathsForChapters(chapters: ZwChapter[]): string[] {
  const paths: string[] = [];
  for (const chapter of chapters) {
    for (const scene of chapter.scenes) {
      paths.push(`${PROJECT_CHAPTERS_DIR}/${chapter.folder}/${scene.file}`);
    }
  }
  return paths;
}

/** Ordered `Chapters/<folder>/<scene file>` paths in flat manuscript order.
 * Acts contribute nothing of their own; only the chapters under them do. */
export function sceneFilesInOrder(manuscript: ManuscriptItem[]): string[] {
  return scenePathsForChapters(flattenChapters(manuscript));
}

/** Extracts `<folder>` from a `Chapters/<folder>/<scene file>` relative path. */
export function chapterFolderFromScenePath(path: string): Maybe<string> {
  const prefix = `${PROJECT_CHAPTERS_DIR}/`;
  if (path.startsWith(prefix) === false) return nothing;
  const rest = path.slice(prefix.length);
  const idx = rest.lastIndexOf("/");
  if (idx <= 0) return nothing;
  return just(rest.slice(0, idx));
}

/** Extracts `<scene file>` from a `Chapters/<folder>/<scene file>` relative path. */
export function sceneFileFromScenePath(path: string): Maybe<string> {
  const folderM = chapterFolderFromScenePath(path);
  return matchMaybe(folderM, {
    nothing: () => nothing,
    just: (folder) => {
      if (folder === "") return nothing;
      const prefix = `${PROJECT_CHAPTERS_DIR}/${folder}/`;
      return path.startsWith(prefix)
        ? just(path.slice(prefix.length))
        : nothing;
    },
  });
}

/** Basename of a project subdir document if `path` points at one. */
export function sidecarNameFromActivePath(
  path: Maybe<string>,
  dir: string,
): Maybe<string> {
  return matchMaybe(path, {
    nothing: () => nothing,
    just: (activePath) => {
      if (activePath === "") return nothing;
      const normalized = activePath.replace(/\\/g, "/");
      const marker = `/${dir}/`;
      const idx = normalized.lastIndexOf(marker);
      if (idx !== -1) {
        const name = normalized.slice(idx + marker.length);
        return name !== "" ? just(name) : nothing;
      }
      if (normalized.startsWith(`${dir}/`)) {
        const name = normalized.slice(dir.length + 1);
        return name !== "" ? just(name) : nothing;
      }
      return nothing;
    },
  });
}

/** Basename of a Notes/ file if `path` points at one. */
export function noteNameFromActivePath(path: Maybe<string>): Maybe<string> {
  return sidecarNameFromActivePath(path, PROJECT_NOTES_DIR);
}

/** Basename of a Database/ file if `path` points at one. */
export function databaseNameFromActivePath(path: Maybe<string>): Maybe<string> {
  return sidecarNameFromActivePath(path, PROJECT_DATABASE_DIR);
}

/** True when `relative` is manuscript-tab content (a scene). */
export function isManuscriptTabFile(relative: Maybe<string>): boolean {
  return matchMaybe(relative, {
    nothing: () => false,
    just: (rel) => {
      if (rel === "") return false;
      if (isJust(databaseNameFromActivePath(just(rel)))) return false;
      if (isJust(noteNameFromActivePath(just(rel)))) return false;
      return isJust(chapterFolderFromScenePath(rel));
    },
  });
}

/** Manuscript sticky path — prefers an explicit manuscript file, else last sticky. */
export function manuscriptResumeFile(
  activeFile: Maybe<string>,
  lastManuscriptFile: Maybe<string>,
): Maybe<string> {
  if (isJust(activeFile) && isManuscriptTabFile(activeFile)) {
    return activeFile;
  }
  if (isJust(lastManuscriptFile) && isManuscriptTabFile(lastManuscriptFile)) {
    return lastManuscriptFile;
  }
  return nothing;
}

/** Resolve a stable chapter folder to its user-facing name. */
export function chapterName(
  chapters: readonly ZwChapter[],
  folder: string,
): string {
  return chapters.find((chapter) => chapter.folder === folder)?.name ?? "";
}
